import { useState } from 'react';
import { Check, ArrowRight, HelpCircle, Sparkles, Zap, Building } from 'lucide-react';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'http://localhost:3200';
interface Plan {
    name: string;
    description: string;
    monthlyPrice: number | null;
    badge?: string;
    icon: typeof Zap;
    gradientClass: string;
    features: string[];
    cta: string;
    popular?: boolean;
}

const PLANS: Plan[] = [
    {
        name: 'Starter',
        description: 'For small businesses getting started with voice AI.',
        monthlyPrice: 99,
        icon: Zap,
        gradientClass: 'grad-stardust',
        cta: 'Start Free Trial',
        features: [
            '1 Voice Agent',
            '500 minutes/month',
            'Standard engine only',
            'Inbound calls',
            'Basic analytics',
            'Email support',
            'CASL compliant',
        ],
    },
    {
        name: 'Growth',
        description: 'For growing teams that need more power and flexibility.',
        monthlyPrice: 299,
        badge: 'Most Popular',
        icon: Sparkles,
        gradientClass: 'grad-nova-plasma',
        cta: 'Start Free Trial',
        popular: true,
        features: [
            '5 Voice Agents',
            '2,500 minutes/month',
            'Standard + Realtime engines',
            'Inbound & Outbound calls',
            'Advanced analytics',
            'Webhook integrations',
            'Priority support',
            'Custom voices',
            'A/B testing',
        ],
    },
    {
        name: 'Enterprise',
        description: 'For organizations with custom requirements at scale.',
        monthlyPrice: null,
        icon: Building,
        gradientClass: 'grad-solar-ember',
        cta: 'Contact Sales',
        features: [
            'Unlimited Agents',
            'Custom minute packages',
            'All engines (incl. Personaplex)',
            'Dedicated infrastructure',
            'SSO & SAML',
            'Custom compliance rules',
            'SLA guarantee',
            'Dedicated CSM',
            'API & SDK access',
            'White-label option',
        ],
    },
];

const FAQS = [
    { q: 'How does the free trial work?', a: 'You get 14 days of the Growth plan completely free — no credit card required. At the end of the trial, choose the plan that fits your needs.' },
    { q: 'What counts as a "minute"?', a: 'A minute is one minute of active call time. This includes both inbound and outbound calls. Voicemail detection and failed connections are not counted.' },
    { q: 'Can I switch plans anytime?', a: 'Yes. Upgrade or downgrade at any time. Changes take effect immediately and billing is prorated.' },
    { q: 'Do you offer annual pricing?', a: 'Yes — annual plans come with a 20% discount. Contact us for enterprise annual agreements.' },
    { q: 'What telephony providers are supported?', a: 'We support Retell, Twilio, and Vonage out of the box. Custom providers can be integrated on Enterprise plans.' },
    { q: 'Is my data secure?', a: 'Absolutely. All data is encrypted at rest and in transit. We\'re SOC 2 Type II compliant and support CASL, TCPA, and PIPEDA.' },
];

/* ── Pricing Calculator ── */
function PricingCalculator() {
    const [minutes, setMinutes] = useState(1000);
    const [agents, setAgents] = useState(3);

    const perMinute = 0.12;
    const perAgent = 29;
    const estimated = minutes * perMinute + agents * perAgent;

    return (
        <div className="glass" style={{ borderRadius: '20px', padding: '2.5rem', maxWidth: '600px', margin: '0 auto' }}>
            <h3 className="font-display font-semibold text-starlight text-center" style={{ fontSize: '1.125rem', marginBottom: '2rem' }}>
                Estimate Your Cost
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {/* Minutes Slider */}
                <div>
                    <div className="flex justify-between" style={{ marginBottom: '0.75rem' }}>
                        <span className="text-stardust" style={{ fontSize: '0.875rem', opacity: 0.7 }}>Monthly Minutes</span>
                        <span className="font-mono text-starlight" style={{ fontSize: '0.875rem' }}>{minutes.toLocaleString()}</span>
                    </div>
                    <input
                        type="range"
                        min={100}
                        max={10000}
                        step={100}
                        value={minutes}
                        onChange={e => setMinutes(Number(e.target.value))}
                    />
                    <div className="flex justify-between text-stardust" style={{ fontSize: '0.7rem', opacity: 0.4, marginTop: '0.25rem' }}>
                        <span>100</span><span>10,000</span>
                    </div>
                </div>

                {/* Agents Slider */}
                <div>
                    <div className="flex justify-between" style={{ marginBottom: '0.75rem' }}>
                        <span className="text-stardust" style={{ fontSize: '0.875rem', opacity: 0.7 }}>Number of Agents</span>
                        <span className="font-mono text-starlight" style={{ fontSize: '0.875rem' }}>{agents}</span>
                    </div>
                    <input
                        type="range"
                        min={1}
                        max={20}
                        step={1}
                        value={agents}
                        onChange={e => setAgents(Number(e.target.value))}
                    />
                    <div className="flex justify-between text-stardust" style={{ fontSize: '0.7rem', opacity: 0.4, marginTop: '0.25rem' }}>
                        <span>1</span><span>20</span>
                    </div>
                </div>

                {/* Result */}
                <div style={{ paddingTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
                    <p className="text-stardust" style={{ fontSize: '0.8rem', opacity: 0.5, marginBottom: '0.5rem' }}>Estimated monthly cost</p>
                    <p className="font-display font-bold gradient-text" style={{ fontSize: '2.5rem' }}>
                        ${Math.round(estimated).toLocaleString()}
                    </p>
                    <p className="text-stardust" style={{ fontSize: '0.7rem', opacity: 0.4, marginTop: '0.5rem' }}>
                        ${perMinute}/min + ${perAgent}/agent/mo · Actual costs may vary
                    </p>
                </div>
            </div>
        </div>
    );
}

export default function Pricing() {
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    return (
        <div style={{ paddingTop: '7rem', paddingBottom: '4rem' }}>
            {/* Header */}
            <section className="section-container text-center" style={{ marginBottom: '4rem' }}>
                <h1
                    className="font-display font-extrabold text-starlight"
                    style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem', lineHeight: 1.15 }}
                >
                    Simple, <span className="gradient-text">transparent</span> pricing
                </h1>
                <p className="text-stardust" style={{ fontSize: '1.125rem', opacity: 0.7, maxWidth: '540px', margin: '0 auto' }}>
                    No hidden fees. No contracts. Start free and scale as you grow.
                </p>
            </section>

            {/* Plans */}
            <section className="section-container" style={{ marginBottom: '6rem' }}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '1.5rem',
                        maxWidth: '1100px',
                        margin: '0 auto',
                        alignItems: 'start',
                    }}
                >
                    {PLANS.map((plan, i) => (
                        <div
                            key={i}
                            className={`animate-fade-in-up ${plan.popular ? 'glass-strong glow-nova' : 'glass glass-hover'}`}
                            style={{
                                borderRadius: '20px',
                                padding: '2rem',
                                animationDelay: `${i * 100}ms`,
                                position: 'relative',
                                transform: plan.popular ? 'scale(1.03)' : 'none',
                            }}
                        >
                            {plan.badge && (
                                <span
                                    className="grad-nova-plasma"
                                    style={{
                                        position: 'absolute',
                                        top: '-12px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        padding: '0.25rem 0.875rem',
                                        borderRadius: '999px',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {plan.badge}
                                </span>
                            )}

                            {/* Icon */}
                            <div className={`icon-box ${plan.gradientClass}`} style={{ marginBottom: '1rem' }}>
                                <plan.icon style={{ width: 20, height: 20, color: 'white' }} />
                            </div>

                            {/* Name + description */}
                            <h3 className="font-display font-bold text-starlight" style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>
                                {plan.name}
                            </h3>
                            <p className="text-stardust" style={{ fontSize: '0.8125rem', opacity: 0.6, marginBottom: '1.5rem' }}>
                                {plan.description}
                            </p>

                            {/* Price */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                {plan.monthlyPrice !== null ? (
                                    <div className="flex items-baseline" style={{ gap: '0.25rem' }}>
                                        <span className="font-display font-bold text-starlight" style={{ fontSize: '2.5rem' }}>
                                            ${plan.monthlyPrice}
                                        </span>
                                        <span className="text-stardust" style={{ fontSize: '0.8125rem', opacity: 0.5 }}>/month</span>
                                    </div>
                                ) : (
                                    <span className="font-display font-bold text-starlight" style={{ fontSize: '1.75rem' }}>
                                        Custom
                                    </span>
                                )}
                            </div>

                            {/* CTA Button */}
                            <a
                                href={`${PORTAL_URL}/signup`}
                                className={plan.popular ? 'btn-primary glow-cta' : 'btn-secondary'}
                                style={{
                                    display: 'block',
                                    textAlign: 'center',
                                    width: '100%',
                                    padding: '0.75rem',
                                    borderRadius: '12px',
                                    marginBottom: '1.5rem',
                                    justifyContent: 'center',
                                }}
                            >
                                {plan.cta}
                            </a>

                            {/* Features */}
                            <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {plan.features.map((feat, j) => (
                                    <li key={j} className="flex items-start" style={{ gap: '0.5rem', fontSize: '0.8125rem', color: 'var(--color-stardust)', opacity: 0.7 }}>
                                        <Check style={{ width: 16, height: 16, color: 'var(--color-pulse)', flexShrink: 0, marginTop: 2 }} />
                                        {feat}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            {/* Calculator */}
            <section className="mesh-gradient" style={{ padding: '5rem 0' }}>
                <div className="section-container">
                    <div className="text-center" style={{ marginBottom: '3rem' }}>
                        <h2 className="font-display font-bold text-starlight" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '0.75rem' }}>
                            Build your own estimate
                        </h2>
                        <p className="text-stardust" style={{ opacity: 0.7 }}>
                            Adjust the sliders to see what Lumina would cost for your usage.
                        </p>
                    </div>
                    <PricingCalculator />
                </div>
            </section>

            {/* FAQs */}
            <section style={{ padding: '5rem 0' }}>
                <div className="section-container" style={{ maxWidth: '720px' }}>
                    <h2 className="font-display font-bold text-starlight text-center" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '3rem' }}>
                        Frequently asked questions
                    </h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {FAQS.map((faq, i) => (
                            <div key={i} className="glass" style={{ borderRadius: '14px', overflow: 'hidden' }}>
                                <button
                                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    className="flex items-center justify-between w-full text-left"
                                    style={{
                                        padding: '1rem 1.5rem',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: 'var(--color-starlight)',
                                        fontSize: '0.9rem',
                                        fontWeight: 500,
                                        fontFamily: 'var(--font-body)',
                                        transition: 'background-color 200ms',
                                    }}
                                >
                                    <span style={{ paddingRight: '1rem' }}>{faq.q}</span>
                                    <HelpCircle
                                        style={{
                                            width: 18,
                                            height: 18,
                                            flexShrink: 0,
                                            color: openFaq === i ? 'var(--color-nova)' : 'var(--color-stardust)',
                                            opacity: openFaq === i ? 1 : 0.4,
                                            transform: openFaq === i ? 'rotate(45deg)' : 'none',
                                            transition: 'all 200ms',
                                        }}
                                    />
                                </button>
                                {openFaq === i && (
                                    <div
                                        className="animate-fade-in-up"
                                        style={{
                                            padding: '0 1.5rem 1.25rem',
                                            fontSize: '0.8125rem',
                                            color: 'var(--color-stardust)',
                                            opacity: 0.65,
                                            lineHeight: 1.65,
                                        }}
                                    >
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section style={{ padding: '4rem 0' }}>
                <div className="section-container text-center" style={{ maxWidth: '600px' }}>
                    <h2 className="font-display font-bold text-starlight" style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                        Ready to get started?
                    </h2>
                    <a href={`${PORTAL_URL}/signup`} className="btn-primary glow-cta" style={{ padding: '0.875rem 2rem', fontSize: '1rem' }}>
                        Start Free — 14 Day Trial
                        <ArrowRight style={{ width: 18, height: 18 }} />
                    </a>
                </div>
            </section>
        </div>
    );
}
